import React, { useCallback } from 'react';
import { useResizeDetector } from 'react-resize-detector';
import useMergedRefs from 'hooks/useMergedRefs';
import useScrollOffset from 'hooks/useScrollOffset';
import * as S from './styles';
import { FadingContainerProps } from './types';

/**
 * A container that fades at the edges when there is overflow to indicate to the
 * user that the element is scrollable.
 */
function FadingContainer<
  TScroll extends HTMLElement = HTMLDivElement,
  TContent extends HTMLElement = HTMLDivElement,
>({
  axis,
  backgroundColor,
  children,
  className,
  style,
  threshold = 0.25,
}: FadingContainerProps<TScroll, TContent>) {
  const { calculateScrollPercentage, contentRef, progress, scrollRef } =
    useScrollOffset<TScroll, TContent>();

  const { ref: containerRef } = useResizeDetector({
    onResize: calculateScrollPercentage,
  });

  const checkRef = useCallback((el: TScroll) => {
    if (el && !(el instanceof HTMLElement)) {
      throw new Error(
        'FadingContainer expects its only child to have an HTMLElement ref',
      );
    }
  }, []);

  const mergedScrollRef = useMergedRefs([scrollRef, checkRef]);

  return (
    <S.Root
      $axis={axis}
      $backgroundColor={backgroundColor}
      className={className}
      $end={(progress[axis] ?? Infinity) <= 1 - threshold}
      ref={containerRef}
      $start={(progress[axis] ?? -Infinity) >= threshold}
      style={style}
    >
      {typeof children === 'function'
        ? children(mergedScrollRef, contentRef)
        : React.isValidElement(children) &&
          React.cloneElement(children, {
            ref: mergedScrollRef,
          })}
    </S.Root>
  );
}

export default FadingContainer;
